import React, { useState, useEffect } from 'react';
import { Clock, Calendar } from 'lucide-react';

const PRESETS = [
  { label: 'Diario 9h', value: '0 9 * * *' },
  { label: 'Diario 10h', value: '0 10 * * *' },
  { label: 'Diario 12h', value: '0 12 * * *' },
  { label: 'Diario 18h', value: '0 18 * * *' },
  { label: 'Seg-Sex 9h', value: '0 9 * * 1-5' },
  { label: 'Seg-Sex 18h', value: '0 18 * * 1-5' },
  { label: 'Segunda 9h', value: '0 9 * * 1' },
  { label: 'Sexta 17h', value: '0 17 * * 5' },
  { label: 'Personalizar', value: 'custom' },
];

const DAYS_OF_WEEK = [
  { label: 'Dom', value: '0' },
  { label: 'Seg', value: '1' },
  { label: 'Ter', value: '2' },
  { label: 'Qua', value: '3' },
  { label: 'Qui', value: '4' },
  { label: 'Sex', value: '5' },
  { label: 'Sab', value: '6' },
];

const COMMON_HOURS = [
  { label: '6h', value: '6' },
  { label: '7h', value: '7' },
  { label: '8h', value: '8' },
  { label: '9h', value: '9' },
  { label: '10h', value: '10' },
  { label: '11h', value: '11' },
  { label: '12h', value: '12' },
  { label: '13h', value: '13' },
  { label: '14h', value: '14' },
  { label: '15h', value: '15' },
  { label: '16h', value: '16' },
  { label: '17h', value: '17' },
  { label: '18h', value: '18' },
  { label: '19h', value: '19' },
  { label: '20h', value: '20' },
  { label: '21h', value: '21' },
];

const MINUTES = [
  { label: ':00', value: '0' },
  { label: ':15', value: '15' },
  { label: ':30', value: '30' },
  { label: ':45', value: '45' },
];

const isHourInCommonList = (hour) => COMMON_HOURS.some((h) => h.value === hour);
const isMinuteInCommonList = (minute) => MINUTES.some((m) => m.value === minute);

const CronExpressionBuilder = ({ value, onChange }) => {
  const [mode, setMode] = useState('preset');
  const [selectedPreset, setSelectedPreset] = useState('');
  const [customHour, setCustomHour] = useState('9');
  const [customMinute, setCustomMinute] = useState('0');
  const [customDays, setCustomDays] = useState(['1', '2', '3', '4', '5']);
  const [showCustomHourInput, setShowCustomHourInput] = useState(false);
  const [showCustomMinuteInput, setShowCustomMinuteInput] = useState(false);

  useEffect(() => {
    if (value) {
      const matchingPreset = PRESETS.find((p) => p.value === value);
      if (matchingPreset && matchingPreset.value !== 'custom') {
        setMode('preset');
        setSelectedPreset(value);
      } else {
        setMode('custom');
        setSelectedPreset('custom');
        parseCronExpression(value);
      }
    }
  }, []);

  const parseCronExpression = (cron) => {
    const parts = cron.split(' ');
    if (parts.length >= 5) {
      const minute = parts[0] === '*' ? '0' : parts[0];
      const hour = parts[1] === '*' ? '9' : parts[1];

      setCustomMinute(minute);
      setCustomHour(hour);
      setShowCustomMinuteInput(!isMinuteInCommonList(minute));
      setShowCustomHourInput(!isHourInCommonList(hour));

      if (parts[4] === '*') {
        setCustomDays(['0', '1', '2', '3', '4', '5', '6']);
      } else if (parts[4].includes('-')) {
        const [start, end] = parts[4].split('-').map(Number);
        const days = [];
        for (let i = start; i <= end; i++) {
          days.push(i.toString());
        }
        setCustomDays(days);
      } else {
        setCustomDays(parts[4].split(','));
      }
    }
  };

  const buildCronExpression = () => {
    const daysValue =
      customDays.length === 7
        ? '*'
        : customDays.length === 0
        ? '*'
        : customDays.sort((a, b) => Number(a) - Number(b)).join(',');

    return `${customMinute} ${customHour} * * ${daysValue}`;
  };

  const handlePresetChange = (presetValue) => {
    setSelectedPreset(presetValue);
    if (presetValue === 'custom') {
      setMode('custom');
      const cron = buildCronExpression();
      onChange(cron);
    } else {
      setMode('preset');
      onChange(presetValue);
    }
  };

  const handleCustomChange = () => {
    const cron = buildCronExpression();
    onChange(cron);
  };

  const toggleDay = (dayValue) => {
    setCustomDays((prev) => {
      const newDays = prev.includes(dayValue)
        ? prev.filter((d) => d !== dayValue)
        : [...prev, dayValue];
      return newDays;
    });
  };

  useEffect(() => {
    if (mode === 'custom') {
      handleCustomChange();
    }
  }, [customHour, customMinute, customDays, mode]);

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-textPrimary mb-2">
          <Clock className="inline h-4 w-4 mr-1" />
          Frequencia
        </label>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((preset) => (
            <button
              key={preset.value}
              type="button"
              onClick={() => handlePresetChange(preset.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                selectedPreset === preset.value
                  ? 'bg-violet-600 text-white'
                  : 'bg-violet-500/10 text-grayText hover:bg-violet-500/20'
              }`}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {mode === 'custom' && (
        <div className="space-y-4 p-4 bg-violet-500/5 rounded-lg border border-violet-500/20">
          <div>
            <label className="block text-sm font-medium text-textPrimary mb-2">
              <Clock className="inline h-4 w-4 mr-1" />
              Horario
            </label>
            <div className="flex flex-wrap gap-2 items-center">
              {COMMON_HOURS.map((hour) => (
                <button
                  key={hour.value}
                  type="button"
                  onClick={() => {
                    setCustomHour(hour.value);
                    setShowCustomHourInput(false);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    customHour === hour.value && !showCustomHourInput
                      ? 'bg-violet-600 text-white'
                      : 'bg-violet-500/10 text-grayText hover:bg-violet-500/20'
                  }`}
                >
                  {hour.label}
                </button>
              ))}
              {showCustomHourInput ? (
                <input
                  type="number"
                  min="0"
                  max="23"
                  value={customHour}
                  onChange={(e) => {
                    const val = Math.min(23, Math.max(0, parseInt(e.target.value) || 0));
                    setCustomHour(val.toString());
                  }}
                  className="w-16 px-2 py-1.5 rounded-lg text-sm font-medium bg-violet-600 text-white border border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-400 text-center"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setShowCustomHourInput(true)}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors bg-violet-500/10 text-grayText hover:bg-violet-500/20"
                >
                  Outro
                </button>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-textPrimary mb-2">
              Minuto
            </label>
            <div className="flex flex-wrap gap-2 items-center">
              {MINUTES.map((minute) => (
                <button
                  key={minute.value}
                  type="button"
                  onClick={() => {
                    setCustomMinute(minute.value);
                    setShowCustomMinuteInput(false);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    customMinute === minute.value && !showCustomMinuteInput
                      ? 'bg-violet-600 text-white'
                      : 'bg-violet-500/10 text-grayText hover:bg-violet-500/20'
                  }`}
                >
                  {minute.label}
                </button>
              ))}
              {showCustomMinuteInput ? (
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={customMinute}
                  onChange={(e) => {
                    const val = Math.min(59, Math.max(0, parseInt(e.target.value) || 0));
                    setCustomMinute(val.toString());
                  }}
                  className="w-16 px-2 py-1.5 rounded-lg text-sm font-medium bg-violet-600 text-white border border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-400 text-center"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setShowCustomMinuteInput(true)}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors bg-violet-500/10 text-grayText hover:bg-violet-500/20"
                >
                  Outro
                </button>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-textPrimary mb-2">
              <Calendar className="inline h-4 w-4 mr-1" />
              Dias da semana
            </label>
            <div className="flex flex-wrap gap-2">
              {DAYS_OF_WEEK.map((day) => (
                <button
                  key={day.value}
                  type="button"
                  onClick={() => toggleDay(day.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    customDays.includes(day.value)
                      ? 'bg-violet-600 text-white'
                      : 'bg-violet-500/10 text-grayText hover:bg-violet-500/20'
                  }`}
                >
                  {day.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CronExpressionBuilder;
